import { memo, useMemo, useState } from 'react';
import type { TrendResultT } from '@mentimeter/visualizations';
import { Trends as TrendsVisualization } from '@mentimeter/visualizations';
import type { VisualizationThemeT } from '@mentimeter/question-modules-types';
import { useRagnar } from '@mentimeter/ragnar-react';
import { usePresentationContext } from '../../legacy-context/presentationContext';
import { VisualizationUpdater } from '../../shared/components/visualizations-legacy/VisualizationUpdater';
import { SvgElement } from '../../shared/components/visualizations-legacy/SvgElement';
import { getBarColors } from '../../shared/utils/getColors';
import { useUniqueSelector } from '../../shared/hooks/useUniqueSelector';
import { trendsTestVotes } from './utils/getChoicesTrends';
import type { Choice } from './types';

type Props = VisualizationThemeT & {
  selector: string;
  results: TrendResultT[];
  choices: Choice[];
  backgroundColor: string;
};

const TrendsComponent = memo<Props>(
  ({
    selector,
    results,
    fillColors,
    textColor,
    backgroundColor,
    choices,
  }: Props) => {
    const [hoverId, setHoverId] = useState<null | string>(null);

    const trends = new TrendsVisualization({
      selector: `#${selector}`,
      results,
      colors: getBarColors(fillColors, backgroundColor),
      textColor,
      type: 'choices',
      hoverId,
      setHoverId,
      choices,
    });

    return (
      <SvgElement>
        <VisualizationUpdater updateFn={trends.update} />
        <g id={selector} data-testid="trends-graph" />
      </SvgElement>
    );
  },
);

export const TrendsDataHandler = ({
  choices,
  shouldHideResults,
}: {
  choices: Choice[];
  shouldHideResults: boolean;
}) => {
  const { useTrends, isPreviewResultsEnabled } = usePresentationContext();
  const {
    theme: {
      visualizationColors: { fillColors, textColor, backgroundColor },
    },
  } = useRagnar();
  const selector = useUniqueSelector();

  const { results: currentTrendResults } = useTrends();

  // Make sure it doesn't create new test votes on each render
  const trendsTestResults = useMemo(
    () => trendsTestVotes({ choices }),
    [choices],
  );

  const trendResults = shouldHideResults
    ? []
    : isPreviewResultsEnabled
      ? trendsTestResults
      : currentTrendResults;

  return (
    <TrendsComponent
      selector={selector}
      fillColors={fillColors}
      textColor={textColor}
      backgroundColor={backgroundColor}
      results={trendResults}
      choices={choices}
    />
  );
};
